import {
  Alert,
  Button,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useClipboard } from "@mantine/hooks";
import { IconCheck, IconCopy, IconExternalLink } from "@tabler/icons-react";
import { useState } from "react";
import { useAddPublicAttandee } from "../../../../api/webinar/useWebinar";
import { IWebinar } from "../../../../models/interfaces/IWebinar.interface";
import { attendeeValidationSchema } from "../../../../models/schemas/Webinar.schema";

type WebinarModalProps = {
  event: IWebinar | null;
  opened: boolean;
  onClose: () => void;
};

export function ShareWebinarModal({
  event,
  opened,
  onClose,
  shareUrl,
}: WebinarModalProps & { shareUrl: string }) {
  const clipboard = useClipboard({ timeout: 1500 });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onExitTransitionEnd={clipboard.reset}
      centered
      title={<Text fw={700}>Share event</Text>}
    >
      <Stack gap="sm">
        <Text fz="sm" c="dimmed" lineClamp={2}>
          {event?.event_title}
        </Text>
        <TextInput
          readOnly
          aria-label="Event link"
          value={shareUrl}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button
          fullWidth
          leftSection={
            clipboard.copied ? <IconCheck size={16} /> : <IconCopy size={16} />
          }
          onClick={() => clipboard.copy(shareUrl)}
        >
          {clipboard.copied ? "Link copied" : "Copy link"}
        </Button>
        {clipboard.error && (
          <Text fz="xs" c="dimmed" ta="center">
            Couldn't copy automatically. Select the link above to copy it.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

export function JoinWebinarModal({ event, opened, onClose }: WebinarModalProps) {
  const { mutateAsync: addAttendee, isPending } = useAddPublicAttandee();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [webinarLink, setWebinarLink] = useState<string | null>(null);
  const form = useForm({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      contact_number: "",
    },
    validate: yupResolver(attendeeValidationSchema),
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!event) return;

    setSubmitError(null);
    try {
      await addAttendee({
        webinar_event_identifier: event.identifier,
        ...values,
        contact_number: values.contact_number.trim(),
        attendee_type: "outsider",
      });
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.errors?.[0]?.message ??
          "Unable to register for this webinar. Please try again."
      );
      return;
    }

    // Browsers (mobile Safari in particular) block pop-ups opened after an
    // async request, so fall back to a link the attendee can tap themselves.
    if (window.open(event.event_detail, "_blank")) {
      onClose();
    } else {
      setWebinarLink(event.event_detail);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onExitTransitionEnd={() => {
        form.reset();
        setSubmitError(null);
        setWebinarLink(null);
      }}
      centered
      title={<Text fw={700}>Join webinar</Text>}
    >
      {webinarLink ? (
        <Stack align="center" ta="center" gap="xs" py="sm">
          <ThemeIcon color="teal" variant="light" radius="xl" size={48}>
            <IconCheck size={26} />
          </ThemeIcon>
          <Text fw={600} mt="xs">
            You're registered
          </Text>
          <Text fz="sm" c="dimmed">
            Open the webinar to join now.
          </Text>
          <Button
            component="a"
            href={webinarLink}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            mt="sm"
            rightSection={<IconExternalLink size={16} />}
            onClick={onClose}
          >
            Open webinar
          </Button>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
          <Stack gap="sm">
            <Text fz="sm" c="dimmed" lineClamp={2}>
              {event?.event_title}
            </Text>
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
              <TextInput
                withAsterisk
                label="First name"
                autoComplete="given-name"
                {...form.getInputProps("first_name")}
              />
              <TextInput
                withAsterisk
                label="Last name"
                autoComplete="family-name"
                {...form.getInputProps("last_name")}
              />
            </SimpleGrid>
            <TextInput
              withAsterisk
              label="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              {...form.getInputProps("email")}
            />
            <TextInput
              withAsterisk
              label="Phone number"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              {...form.getInputProps("contact_number")}
            />
            {submitError && (
              <Alert color="red" variant="light">
                {submitError}
              </Alert>
            )}
            <Button type="submit" fullWidth mt="xs" loading={isPending}>
              Join webinar
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
